import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges, OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {buildContactInfoList, DigitalBusinessCard} from '../../../digital-business-card/digital-business-card.model';
import {DBC_FORM_FIELD, ONBOARDING_CONTACT_ORDER} from '../../dbc-onboard-v2.utils';
import {IMAGE_TYPE} from '../dbc-mobile-form/dbc-mobile-form.component';
import {fadeIn} from '../../dbc-onboarding-v2-animations';
import {hexToRGBA} from '../../../preview-templates/templateUtils';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';

@Component({
    selector: 'app-dbc-desktop-form',
    templateUrl: './dbc-desktop-form.component.html',
    styleUrls: ['./dbc-desktop-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [fadeIn]
})
export class DbcDesktopFormComponent implements AfterViewInit, OnChanges, OnDestroy {

    @Input() dbc: DigitalBusinessCard;
    @Input() fullPhoneNumber: string;
    @Input() countryNameCode: string;
    @Input() phoneNumber: string;
    @Input() colorOptions: string[];
    @Input() logoUrl: string;
    @Input() userImageUrl: string;
    @Input() isFirstNameMissing: boolean;
    @Input() isInvalidEmail: boolean;
    @Input() defaultColorPickerBackground: string;
    @Input() layoutPrimaryColor: string;
    @Input() showSaveLoader: boolean;
    @Input() emailInputErrorMessage: string;

    @Output() inputValueChange = new EventEmitter<{value: string, type: DBC_FORM_FIELD}>();
    @Output() onCountryFlagChange = new EventEmitter<string>();
    @Output() imageSelected = new EventEmitter<{ type: IMAGE_TYPE, file: File }>();
    @Output() onSaveCardClicked = new EventEmitter<boolean>();
    @Output() openEditImageModal = new EventEmitter<IMAGE_TYPE>();

    @ViewChild('digitalBusinessCardPreview', { static: false }) digitalBusinessCardPreview: ElementRef;

    backgroundImage: string = '../../../assets/img/dbc-onboard/dbc-onboard-bg.png';
    previewBackgroundColor: string = 'rgba(255, 255, 255, 1)';
    expandedForm: boolean = false;
    hasEdited: boolean = false;

    dbcContentPreviewChange: Subject<boolean> = new Subject<boolean>();
    ngUnsubscribe: Subject<any> = new Subject();


    constructor() {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['dbc']?.currentValue || changes['userImageUrl'] || changes['logoUrl']) {
            this.writeOnIframe(this.digitalBusinessCardPreview);
            this.previewBackgroundColor = hexToRGBA(this.dbc.customizations.background_color, 0.1);
            this.hasEdited = !this.hasAllFieldsEmpty();
        }

        this.dbcContentPreviewChange.pipe(takeUntil(this.ngUnsubscribe), debounceTime(100)).subscribe(content => {
            this.writeOnIframe(this.digitalBusinessCardPreview);
        })
    }

    ngAfterViewInit() {
        this.writeOnIframe(this.digitalBusinessCardPreview);
        this.setupIframeClickListener();
        this.setupPhoneInputClickListener();
        this.setupColorPickerClickListener();
    }

    hasAllFieldsEmpty(): boolean {
        // This function checks if all onboarding fields are empty
        const hasEmptyExpandedFieldValues = !this.dbc.designation
            && !this.dbc.company
            // @ts-ignore
            && (this.dbc.website_v2.length > 0 && !this.dbc.website_v2[0].value)

        if (!hasEmptyExpandedFieldValues) {
            this.expandedForm = true;
        }

        return hasEmptyExpandedFieldValues
            && !this.dbc.first_name
            && !this.dbc.last_name
            // @ts-ignore
            && (this.dbc.phone_v2.length > 0 && !this.dbc.phone_v2[0].value)
            // @ts-ignore
            && (this.dbc.email_v2.length > 0 && !this.dbc.email_v2[0].value)
    }

    setupPhoneInputClickListener() {
        const scrollContainer = document.getElementById('scrollable-form-container');

        const observer = new MutationObserver((mutations, obs) => {
            const flagContainer = document.querySelector('.iti__flag-container');
            if (flagContainer && scrollContainer) {
                flagContainer.addEventListener('click', () => {
                    scrollContainer.scrollTo({
                        top: scrollContainer.scrollHeight - 304,
                        behavior: 'smooth'
                    });
                });
                obs.disconnect();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    setupColorPickerClickListener() {
        const colorPicker = document.querySelector('.color-picker-circle');
        const scrollContainer = document.getElementById('scrollable-form-container');

        if (colorPicker && scrollContainer) {
            colorPicker.addEventListener('click', () => {
                scrollContainer.scrollBy({
                    top: -100,
                    behavior: 'smooth'
                });
            });
        }
    }

    writeOnIframe(element) {
        this.dbc.__contact_info_list__ = buildContactInfoList({contact_info_ordering: ONBOARDING_CONTACT_ORDER, ...this.dbc});

        const iframe = element;
        const iframedoc = iframe?.nativeElement?.contentWindow || iframe?.nativeElement?.contentDocument?.document ||
            iframe?.nativeElement?.contentDocument;

        if (!iframedoc) {
            return;
        }

        iframedoc.document.open();
        const dbc_data = {
            ...this.dbc,
            layout: '1',
            __onboarding_layout__: true,
            __is_all_onboarding_fields_visible__: this.expandedForm
        } // Hardcoding to 1
        iframedoc.document.write(DigitalBusinessCard.getVcardPreviewHTML(dbc_data, false, false));
        iframedoc.document.close();
    }

    setupIframeClickListener(): void {
        const iframe: HTMLIFrameElement = this.digitalBusinessCardPreview.nativeElement;

        iframe.onload = () => {
            const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

            if (iframeDocument) {
                const profileContainer = iframeDocument.getElementById('profileUpload');
                const logoContainer = iframeDocument.getElementById('logoUpload');
                if (profileContainer) {
                    profileContainer.addEventListener('click', (event) => {
                        event.preventDefault();
                        this.onElementClick(event, IMAGE_TYPE.PROFILE)
                    });
                }
                if (logoContainer) {
                    logoContainer.addEventListener('click', (event) => {
                        event.preventDefault();
                        this.onElementClick(event, IMAGE_TYPE.LOGO);
                    });
                }



                const onboardContainer = iframeDocument.body;

                onboardContainer.addEventListener('click', (event: MouseEvent) => {
                    const target = event.target as HTMLElement;
                    if (target.id === 'user-name') {
                        this.onTextElementClick(event, DBC_FORM_FIELD.FIRST_NAME);
                    } else if (target.id === 'user-job') {
                        this.onTextElementClick(event, DBC_FORM_FIELD.DESIGNATION);
                    } else if (target.id === 'user-company') {
                        this.onTextElementClick(event, DBC_FORM_FIELD.COMPANY);
                    } else if (target.id === 'user-email') {
                        this.onTextElementClick(event, DBC_FORM_FIELD.EMAIL);
                    } else if (target.id === 'user-phone') {
                        this.onTextElementClick(event, DBC_FORM_FIELD.PHONE_NUMBER);
                    } else if (target.id === 'user-website') {
                        this.onTextElementClick(event, DBC_FORM_FIELD.WEBSITE);
                    } else if (target.id === 'ellipses-container') {
                        this.onTextElementClick(event, DBC_FORM_FIELD.BACKGROUND_COLOR)
                    }
                });
            }
        };
    }

    onElementClick(event: MouseEvent, elementType: IMAGE_TYPE): void {
        if ((elementType === IMAGE_TYPE.LOGO && !!this.logoUrl) || (elementType === IMAGE_TYPE.PROFILE && !!this.userImageUrl)) {
            this.openEditImageModal.emit(elementType);
        } else {
            let elementId: string;
            if (elementType === IMAGE_TYPE.PROFILE) {
                elementId = 'profile'
            } else {
                elementId = 'logo'
            }
            const fileInput = document.getElementById(`file-input-${elementId}`) as HTMLInputElement;
            if (fileInput) {
                fileInput.click();
            }
        }
    }

    onTextElementClick(event: MouseEvent, field: DBC_FORM_FIELD): void {
        let inputElement = document.getElementById(field) as HTMLInputElement;
        if (field === DBC_FORM_FIELD.PHONE_NUMBER) {
            inputElement = document.getElementById('phone') as HTMLInputElement; // As it is a kaizen input with id as phone.
        }
        if (inputElement) {
            inputElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });

            setTimeout(() => {
                inputElement.focus();
                inputElement.click();
            }, 200);
        }
    }

    onFileSelected(event: Event, type: IMAGE_TYPE): void {
        const inputElement = event.target as HTMLInputElement;
        const file = inputElement.files?.[0];
        if (file) {
            this.imageSelected.emit({ type: type, file });
            inputElement.value = '';
        }
    }


    onCountryChange(countryCallingCode: string) {
        this.onCountryFlagChange.emit(countryCallingCode);
    }

    inputChange(value: string, field: DBC_FORM_FIELD): void {
        this.hasEdited = true;
        this.inputValueChange.emit({value, type: field});
        if (field === DBC_FORM_FIELD.BACKGROUND_COLOR) {
            this.previewBackgroundColor = hexToRGBA(value, 0.1);
        }
    }

    onSaveCard() {
        this.onSaveCardClicked.emit();
    }

    onAddMoreClick() {
        this.expandedForm = true;
        this.dbcContentPreviewChange.next(true);

    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(undefined);
        this.ngUnsubscribe.complete();
    }

    protected readonly DBC_FORM_FIELD = DBC_FORM_FIELD;
    protected readonly IMAGE_TYPE = IMAGE_TYPE;
}
